import { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';

// =============================================================================
// Hook: useBarcodeScanner
// Single source of truth for ALL STRICH SDK lifecycle in the app.
// Consolidates identical boilerplate from BarcodeScanner.jsx,
// VisionGroceryCapture.jsx, and NutritionComparisonMode.jsx.
// Patent claims exercised: P-002 Claims 1-2,6-8
// =============================================================================

// SDK singleton — loaded once, shared across all hook instances
let StrichSDK = null;
let sdkInitialized = false;
let sdkInitPromise = null;

/**
 * @param {Object} options
 * @param {Function} options.fetchWithAuth - Auth-aware fetch function
 * @param {boolean} [options.autoInit=true] - Initialize SDK on mount
 * @returns {Object} { sdkReady, error, isMobile, startCameraScanning, stopCameraScanning, scanImage }
 */
export function useBarcodeScanner({ fetchWithAuth, autoInit = true } = {}) {
  const [sdkReady, setSdkReady] = useState(sdkInitialized);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const barcodeReaderRef = useRef(null);

  // Device detection
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 1024;
      setIsMobile(isMobileDevice || (isTouchDevice && isSmallScreen));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // SDK initialization
  useEffect(() => {
    if (autoInit) {
      initSDK();
    }
    return () => {
      stopCameraScanning();
    };
  }, [autoInit]);

  const initSDK = useCallback(async () => {
    // Already initialized
    if (sdkInitialized) {
      setSdkReady(true);
      return;
    }

    // Another instance is initializing — wait for it
    if (sdkInitPromise) {
      try {
        await sdkInitPromise;
        setSdkReady(true);
      } catch (err) {
        setError(`Scanner initialization failed: ${err.message}`);
      }
      return;
    }

    // First caller — do the init
    sdkInitPromise = (async () => {
      // Fetch license key from backend
      const fetcher = fetchWithAuth || fetch;
      const keyResponse = await fetcher(`${API_BASE}/api/config/strich-key`);
      if (!keyResponse.ok) {
        throw new Error('Failed to fetch STRICH license key');
      }
      const keyData = await keyResponse.json();

      // Dynamic import of STRICH SDK
      if (!StrichSDK) {
        const module = await import('@pixelverse/strichjs-sdk');
        StrichSDK = module;
      }

      // Initialize SDK — guard against double-init
      try {
        await StrichSDK.StrichSDK.initialize(keyData.key);
      } catch (initErr) {
        if (!initErr.message?.includes('already initialized')) {
          throw initErr;
        }
      }

      sdkInitialized = true;
    })();

    try {
      await sdkInitPromise;
      setSdkReady(true);
    } catch (err) {
      console.error('STRICH SDK initialization failed:', err);
      setError(`Scanner initialization failed: ${err.message}. Manual entry available.`);
    }
  }, [fetchWithAuth]);

  /**
   * Start camera-based barcode scanning.
   * @param {Object} options
   * @param {string} options.selector - CSS selector for the host element (e.g. '#scanner-host')
   * @param {Object} [options.engineConfig] - Override engine config (symbologies, etc.)
   * @param {Function} options.onDetection - Called with each detection array
   * @returns {Promise<boolean>} true if started successfully
   */
  const startCameraScanning = useCallback(async ({ selector, engineConfig, onDetection }) => {
    if (!sdkInitialized || !StrichSDK) {
      setError('Scanner not ready. Please wait or use manual entry.');
      return false;
    }

    // Cleanup any previous reader
    await stopCameraScanning();

    try {
      const config = {
        selector,
        engine: engineConfig || {
          symbologies: ['ean13', 'ean8', 'upca', 'upce', 'code128', 'code39'],
          duplicateInterval: 2500
        },
        feedback: { audio: true, vibration: true }
      };

      const reader = new StrichSDK.BarcodeReader(config);
      await reader.initialize();

      reader.detected = onDetection;

      await reader.start();
      barcodeReaderRef.current = reader;
      return true;
    } catch (err) {
      console.error('Scanner start error:', err);
      setError(`Failed to start camera: ${err.message}`);
      return false;
    }
  }, []);

  /**
   * Stop camera scanning and clean up BarcodeReader.
   */
  const stopCameraScanning = useCallback(async () => {
    if (barcodeReaderRef.current) {
      try {
        await barcodeReaderRef.current.destroy();
      } catch (err) {
        console.error('Scanner cleanup error:', err);
      }
      barcodeReaderRef.current = null;
    }
  }, []);

  /**
   * Scan an image file for barcodes (static analysis, no camera).
   * @param {File} file - Image file to scan
   * @param {string[]} [symbologies] - Barcode types to detect
   * @returns {Promise<Array>} Array of detections ({ data, symbology })
   */
  const scanImage = useCallback(async (file, symbologies) => {
    if (!sdkInitialized || !StrichSDK) {
      throw new Error('Scanner SDK not initialized');
    }

    const { ImageScanner } = StrichSDK;
    if (!ImageScanner) {
      throw new Error('ImageScanner not available. STRICH SDK may not be fully loaded.');
    }

    return ImageScanner.scan(file, {
      engine: { symbologies: symbologies || ['ean13', 'ean8', 'upca', 'upce', 'code128', 'code39'] }
    });
  }, []);

  return {
    sdkReady,
    error,
    isMobile,
    initSDK,
    startCameraScanning,
    stopCameraScanning,
    scanImage
  };
}
